import React, { useEffect, useState } from "react";
import { Input } from "@/stories/input/input";
import { CustomizableSlider } from "@/app/components/shared/Custom-Sldier";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import type { ChangeEvent } from 'react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import DonutChart from "./donut-chart";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import Apple from "@/app/assets/icons/apple";
import GooglePlay from "@/app/assets/icons/googleplay";
import { CompoundingFrequency } from "@/app/constants/fd-data";
import { formatIndianCurrency } from "@/app/utils/helper";
import { useRef } from "react";
// import { initBranch, trackEvent } from "@/app/utils/branchio";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { debounce } from "lodash";
import Info from "@/app/assets/icons/info";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";
interface FdCalculatorProps {
    toggleDownloadApp?: (arg0: string) => void;
    renderHeader: boolean;
}

const FdCalculator: React.FC<FdCalculatorProps> = ({ toggleDownloadApp, renderHeader }) => {
    const { cleverTapTrackEvent } = useCleverTapEvents();
    const [totalInvestment, setTotalInvestment] = useState<number>(100000);
    const [investmentInputValue, setInvestmentInputValue] = useState<string>("100000");
    const [investmentSliderValue, setInvestmentSliderValue] = useState<number>(100000);
    const [interestRate, setInterestRate] = useState<number>(6);
    const [timePeriod, setTimePeriod] = useState<number>(5);
    const [isSenior, setIsSenior] = useState<boolean>(false);
    const [isWomen, setIsWomen] = useState<boolean>(false);
    const [cumulativeInt, setCumulativeInt] = useState<boolean>(true);
    const [selectedCompounding, setSelectedCompounding] = useState<string>("Yearly");
    const [interestEarned, setInterestEarned] = useState<number>(0);
    const investmentInputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [inputValue, setInputValue] = useState('');
    // const [maturityAmount, setMaturityAmount] = useState<number>(0);

    const dropdownOptions = Object.values(CompoundingFrequency).map((value) => ({
        key: value,
        value: value,
        label: value,
    }));

    
    useEffect(() => {
        calculateReturns();
    }, [totalInvestment, interestRate, timePeriod, cumulativeInt, selectedCompounding]);

    useEffect(() => {
        return () => {
          debouncedAnalytics.cancel();
        };
    }, []);

    const debouncedAnalytics = debounce((event: string) => {
        cleverTapTrackEvent(event, {});
        // initBranch()
        //   .then(() => {
        //     trackEvent(event, "", "");
        //   })
        //   .catch(error => console.error('Error initializing Branch:', error));
        setAnalyticsEvent(window.location.origin, event);
    }, 1000); //

    const calculateReturns = () => {
        const time = timePeriod / 12; // Convert months to years
        let finalAmount = 0;

        if (!cumulativeInt) {
            // Simple Interest Calculation
            const interest = (totalInvestment * interestRate * time) / 100;
            finalAmount = totalInvestment + interest;
        } else {
            // Compound Interest Calculation
            let n = 1; // Default to yearly compounding

            switch (selectedCompounding) {
                case "Monthly":
                    n = 12;
                    break;
                case "Quarterly":
                    n = 4;
                    break;
                case "Semi-annually":
                    n = 2;
                    break;
                case "Yearly":
                default:
                    n = 1;
                    break;
            }

            finalAmount = totalInvestment * Math.pow(1 + interestRate / (100 * n), n * time);
        }

        // setMaturityAmount(Math.round(finalAmount));
        setInterestEarned(Math.round(finalAmount - totalInvestment));
    };      

    const formatToNumericString = (value: string): string => {
        return value.replace(/[^0-9]/g, "");
    };

    const handleInvestmentInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const cursorPosition = input.selectionStart || 0;
        
        // Remove any non-numeric characters
        const rawValue = formatToNumericString(input.value);
        
        // Allow empty input for clearing
        if (rawValue === "") {
            setInvestmentInputValue("");
            setTotalInvestment(0);
            setInvestmentSliderValue(0);
            return;
        }

        // Convert to number
        const numericValue = parseInt(rawValue);
        
        // Update states if within valid range
        if (numericValue >= 0 && numericValue <= 10000000) {
            setInvestmentInputValue(rawValue);
            setTotalInvestment(numericValue);

            setInvestmentSliderValue(numericValue);
            debouncedAnalytics("FD_FD_Calculator_Invested");
            
            // Calculate new cursor position
            const formattedLength = formatIndianCurrency(numericValue).length;
            const rawLength = rawValue.length;
            const offset = formattedLength - rawLength;
            
            // Restore cursor position after state update
            requestAnimationFrame(() => {
                if (investmentInputRef.current) {
                    const newPosition = Math.min(cursorPosition + offset, formattedLength);
                    investmentInputRef.current.setSelectionRange(newPosition, newPosition);
                }
            });
        }
    };

    const handleInvestmentBlur = () => {
        // If empty or invalid, set to minimum value
        if (investmentInputValue === "" || parseInt(investmentInputValue) < 5000) {
            setTotalInvestment(5000);
            setInvestmentInputValue("5000");
            setInvestmentSliderValue(5000);
            return;
        }

        // If above maximum, set to maximum value
        const numericValue = parseInt(investmentInputValue);
        if (numericValue > 10000000) {
            setTotalInvestment(10000000);
            setInvestmentInputValue("10000000");
            setInvestmentSliderValue(10000000);
            return;
        }

        // Otherwise, keep the current valid value
        setTotalInvestment(numericValue);
        setInvestmentInputValue(numericValue.toString());
        setInvestmentSliderValue(numericValue);
    };

    const handleInvestmentFocus = () => {
        if (investmentInputRef.current) {
            // Show raw numeric value without formatting when focused
            const numericValue = formatToNumericString(investmentInputRef.current.value);
            setInvestmentInputValue(numericValue);
            
            // Select all text on focus
            requestAnimationFrame(() => {
                investmentInputRef.current?.select();
            });
        }
    };


    // Separate handler for slider changes
    const handleInvestmentSlider = (value: number) => {
        const numericValue = Math.min(Math.max(value, 5000), 10000000);
        setInvestmentSliderValue(numericValue);
        setTotalInvestment(numericValue);
        setInvestmentInputValue(numericValue.toString());
        debouncedAnalytics("FD_FD_Calculator_Invested");
    };
    //     type: "event" | "value",
    //     e: React.ChangeEvent<HTMLInputElement> | number
    //   ) => {
    //     // Handle direct number input
    //     if (type === "value" && typeof e === "number") {
    //       const limitedValue = Math.min(e, 25);
    //       setInterestRate(limitedValue);
    //       return;
    //     }
      
    //     // Handle input event
    //     if (type === "event" && typeof e === "object" && "target" in e) {
    //       const inputValue = e.target.value;
          
    //       // Allow empty input to be cleared
    //       if (inputValue === "") {
    //         setInterestRate(0);
    //         return;
    //       }
      
    //       // Only allow numbers and decimals
    //       if (/^\d*\.?\d*$/.test(inputValue)) {
    //         const numericValue = parseFloat(inputValue);
            
    //         // Handle incomplete decimal input (like "1." or ".")
    //         if (inputValue.endsWith(".")) {
    //           return; // Don't update state, let user continue typing
    //         }
      
    //         // Set the value if it's a valid number and within range
    //         if (!isNaN(numericValue) && numericValue <= 25) {
    //           setInterestRate(numericValue);
    //         }
    //       }
    //     }
    // };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
    
        // Allow empty input while focused
        if (value === '') {
            setInputValue('');
            setInterestRate(0);
            return;
        }
    
        // Only allow numbers and decimals
        if (/^\d*\.?\d*$/.test(value)) {
            let numericValue = parseFloat(value);
    
            // If input exceeds max value, replace it with max value
            if (!isNaN(numericValue) && numericValue > 25) {
                numericValue = 25;
                value = '25'; // Ensure input field reflects the max value
            }
    
            setInputValue(value);
    
            // Only update the actual interest rate if it's a complete valid number
            if (!value.endsWith('.')) {
                if (!isNaN(numericValue)) {
                    setInterestRate(numericValue);
                    debouncedAnalytics("FD_FD_Calculator_Interest_Rate");
                }
            }
        }
    };
    
    const handleInputFocus = () => {
        setIsFocused(true);
        // Show the current value when focused
        setInputValue(interestRate.toString());
      };
      
    const handleInputBlur = () => {
        setIsFocused(false);
        
        // Clean up incomplete decimal inputs on blur
        if (inputValue.endsWith('.') || inputValue === '') {
            const numericValue = parseFloat(inputValue || '0');
            setInterestRate(numericValue);
            setInputValue(numericValue.toString());
        }
    };
      
      // Separate handler for the slider
    const handleSliderChange = (value: number[]) => {
        const limitedValue = Math.min(value[0], 25);
        handleSeniorChange(false);
        handleWomenChange(false);
        
        setInterestRate(limitedValue);
        // Update input value if not focused
        if (!isFocused) {
          setInputValue(limitedValue.toString());
        }
        debouncedAnalytics("FD_FD_Calculator_Interest_Rate");
    };

    const handleTenure = (type: "event" | "value", e: React.ChangeEvent<HTMLInputElement> | number) => {
        let value: string;
        
        if (type === "event" && typeof e === 'object' && e !== null && 'target' in e) {
            value = e.target.value.replace(/[^0-9]/g, "");
        } else if (type === "value" && typeof e === "number") {
            value = e.toString();
        } else {
            value = "";
        }
    
        if (value === "") {
            setTimePeriod(0); // Allow clearing the field
            return;
        }
    
        let numericValue = Number(value);
        if (!isNaN(numericValue)) {
            numericValue = Math.min(Math.max(numericValue, 1), 240);
            setTimePeriod(numericValue);
            debouncedAnalytics("FD_FD_Calculator_Tenure");
        }
    };

    const adjustInterestRate = (checked: boolean, setState: React.Dispatch<React.SetStateAction<boolean>>) => {
        setState(checked);
        setInterestRate((prevRate) => {
            const adjustment = checked ? 0.5 : -0.5;
            return Number((prevRate + adjustment).toFixed(1));
        });
    };

    const handleSeniorChange = (checked: boolean) => {
        if (checked === true) {
            debouncedAnalytics("FD_FD_Calculator_Senior_Citizen");
        } 
        adjustInterestRate(checked, setIsSenior)
    };
    const handleWomenChange = (checked: boolean) => {
        if (checked === true) {
            debouncedAnalytics("FD_FD_Calculator_Women");
        } 
        adjustInterestRate(checked, setIsWomen)
    };
    
    return (
        <div
            id="fd-calculator-container"
            className={`m-0 w-full md:mx-auto ${!renderHeader ? '' : 'md:my-[120px] pb-s13'} md:w-[952px] md:pb-0`}
        >
            {renderHeader && <><p
                id="fd-calculator-title"
                className="hidden text-center text-neutral-900 md:block md:text-7xl md:font-semibold md:leading-[67.2px] md:tracking-[-0.56px]"
            >
                FD Calculator
            </p>
            <p
                id="fd-calculator-subtitle"
                className="hidden text-center text-neutral-900 md:mb-20 md:mt-3 md:block md:text-3xl md:font-regular md:leading-[39.84px] md:tracking-[-0.24px]"
            >
                Calculate returns on your investments using our FD calculator
            </p></>}
            <div
                id="calculator-main-box"
                className={`${renderHeader ? 'shadow-[0px_2px_25px_-5px_rgba(0,0,0,0.20),_0px_5px_10px_-6px_rgba(0,0,0,0.10)]' :'md:border md:border-neutral-100'} md:rounded-[40px]`}
            >
                {renderHeader && <p
                    id="earnings-title"
                    className="p-s9 text-center text-xl font-semibold leading-[26px] text-neutral-900 md:px-[40px] md:pb-[24px] md:pt-[28px] md:text-left"
                >
                    Calculate your earnings
                </p>}

                <div
                    id="calculator-input-section"
                    className={`flex w-full grid-cols-2 flex-col gap-10 ${renderHeader ? 'border-t-[1px] px-6 py-s13 md:gap-[86px]' : 'md:gap-[32px]'} border-neutralOpacityDark-10 md:flex-row md:p-s13`}
                >
                    <div id="input-fields-container" className="flex w-full flex-col gap-6">
                        <div id="invested-amount-section" className="flex flex-col duration-500">
                            <div className="mb-1 flex h-[30px] items-center justify-between">
                                <p
                                    id="invested-amount-label"
                                    className="text-base font-regular leading-[22.4px] text-neutral-500"
                                >
                                    Invested amount
                                </p>
                                <div className="flex h-[30px]">
                                    <Input
                                        ref={investmentInputRef}
                                        id="invested-amount-input"
                                        style={{ textAlign: "end", fontWeight: "600" }}
                                        className="!h-[30px] !min-w-max border-none !p-0 !text-end text-lg leading-[29.52px] tracking-[-0.18px] text-neutral-900 focus:outline-none focus:ring-0"
                                        placeholder="Enter amount"
                                        showIcon={false}
                                        inputMode="numeric"
                                        type="text"
                                        value={
                                            investmentInputValue === ""
                                                ? ""
                                                : formatIndianCurrency(
                                                      parseInt(investmentInputValue) || 0,
                                                  )
                                        }
                                        min={5000}
                                        max={10000000}
                                        onChange={handleInvestmentInput}
                                        onFocus={handleInvestmentFocus}
                                        onBlur={handleInvestmentBlur}
                                    />
                                </div>
                            </div>
                            <CustomizableSlider
                                id="invested-amount-slider"
                                value={[investmentSliderValue]}
                                onValueChange={(value: number[]) => {
                                    handleInvestmentSlider(value[0]);
                                }}
                                defaultValue={[5000]}
                                min={5000}
                                max={10000000}
                                step={10000}
                                style={{ fontWeight: "600" }}
                                className="h-2 rounded-lg bg-gray-200 !font-semibold"
                                rangeClassName="bg-brand-primary-500"
                                thumbClassName="border-grey-200"
                            />
                            <div
                                className={`w-full flex transform justify-end items-center gap-2 transition-all duration-500 ${
                                    totalInvestment < 5000 
                                        ? "mb-2 mt-2 translate-y-0 opacity-100"
                                        : "mb-0 mt-2 -translate-y-2 opacity-0"
                                }`}
                            >
                                <Info width="12" height="12" className="text-error-600" />
                                <p className="text-sm text-error-600">Minimum value allowed is 5000</p>
                            </div>
                        </div>

                        <div id="interest-rate-section" className="flex flex-col duration-500">
                            <div className="mb-1 flex h-[30px] items-center justify-between">
                                <p
                                    id="interest-rate-label"
                                    className="text-base font-regular leading-[22.4px] text-neutral-500"
                                >
                                    Interest rate (p.a.)
                                </p>
                                <div className="flex h-[30px]">
                                    <Input
                                        id="interest-rate-input"
                                        style={{ textAlign: "end", fontWeight: "600" }}
                                        className="h-[30px] !font-semibold !max-w-[90px] border-none !p-0 !text-end text-lg leading-[29.52px] tracking-[-0.18px] text-neutral-900 focus:outline-none focus:ring-0"
                                        placeholder="0"
                                        showIcon={false}
                                        showRightIcon={true}
                                        icon={<div className="ml-[1px] font-semibold">%</div>}
                                        inputMode="decimal"
                                        step="any"
                                        pattern="[0-9]*[.,]?[0-9]*"
                                        type="text"
                                        value={isFocused ? inputValue : (interestRate || '')}
                                        min={1}
                                        max={25}
                                        onPaste={(e) => {
                                            e.preventDefault();
                                            return false;
                                        }}
                                        onCopy={(e) => {
                                            e.preventDefault();
                                            return false;
                                        }}
                                        onChange={handleInputChange}
                                        onFocus={handleInputFocus}
                                        onBlur={handleInputBlur}
                                    />
                                </div>
                            </div>
                            <CustomizableSlider
                                id="interest-rate-slider"
                                value={[interestRate]}
                                onValueChange={handleSliderChange}
                                defaultValue={[interestRate]}
                                min={1}
                                max={25}
                                step={0.1}
                                style={{ fontWeight: "600" }}
                                className="h-2 rounded-lg bg-gray-200 !font-semibold"
                                rangeClassName="bg-brand-primary-500"
                                thumbClassName="border-grey-200"
                            />
                            {renderHeader && <div
                                id="special-rates-section"
                                className="mb-6 mt-2 flex h-5 flex-col items-start justify-start gap-[10px] md:mb-0 md:flex-row md:items-center"
                            >
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="senior-citizen-checkbox"
                                        className="h-4 w-4 border border-neutralOpacityDark-20 data-[state=checked]:bg-brand-primary-500"
                                        checked={isSenior}
                                        onCheckedChange={handleSeniorChange}
                                    />
                                    <p
                                        id="senior-citizen-label"
                                        className="text-xs font-regular leading-[19.2px] tracking-[0.12px]"
                                    >
                                        I am a senior citizen{" "}
                                        <span className="text-neutral-300">+0.5%</span>
                                    </p>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="women-checkbox"
                                        className="h-4 w-4 border border-neutralOpacityDark-20 data-[state=checked]:bg-brand-primary-500"
                                        checked={isWomen}
                                        onCheckedChange={handleWomenChange}
                                    />
                                    <p
                                        id="women-label"
                                        className="text-xs font-regular leading-[19.2px] tracking-[0.12px]"
                                    >
                                        I am a woman <span className="text-neutral-300">+0.5%</span>
                                    </p>
                                </div>
                            </div>}
                            <div
                                className={`w-full flex transform justify-end items-center gap-2 transition-all duration-500 ${
                                    interestRate < 1
                                        ? "mb-2 mt-2 translate-y-0 opacity-100"
                                        : "mb-0 mt-2 -translate-y-2 opacity-0"
                                }`}
                            >
                                <Info width="12" height="12" className="text-error-600" />
                                <p className="text-sm text-error-600">Minimum interest should be 1</p>
                            </div>
                        </div>

                        <div id="tenure-section" className="flex flex-col duration-500">
                            <div className="mb-1 flex h-[30px] items-center justify-between">
                                <p
                                    id="tenure-label"
                                    className="text-base font-regular leading-[22.4px] text-neutral-500"
                                >
                                    Tenure in months
                                </p>
                                <div className="flex h-[30px]">
                                <Input
                                    id="tenure-input"
                                    style={{ textAlign: "end", fontWeight: "600" }}
                                    className="h-[30px] !max-w-[90px] border-none !p-0 !text-end text-lg !font-semibold leading-[29.52px] tracking-[-0.18px] text-neutral-900 focus:outline-none focus:ring-0"
                                    placeholder="0"
                                    showIcon={false}
                                    inputMode="numeric"
                                    type="text"
                                    value={timePeriod}
                                    min={1}
                                    max={240}
                                    onPaste={(e) => e.preventDefault()}
                                    onCopy={(e) => e.preventDefault()}
                                    onChange={(e) => handleTenure("event", e)}
                                />
                                </div>
                            </div>
                            <CustomizableSlider
                                id="tenure-slider"
                                value={[timePeriod]}
                                onValueChange={(value: number[]) => {
                                    handleTenure("value", value[0]);
                                }}
                                defaultValue={[timePeriod]}
                                min={1}
                                max={240}
                                step={1}
                                className="h-2 rounded-lg bg-gray-200"
                                rangeClassName="bg-brand-primary-500"
                                thumbClassName="border-grey-200"
                            />
                            <div
                                className={`w-full flex transform justify-end items-center gap-2 transition-all duration-500 ${
                                    timePeriod === 0
                                        ? "mb-2 mt-2 translate-y-0 opacity-100"
                                        : "mb-0 mt-2 -translate-y-2 opacity-0"
                                }`}
                            >
                                <Info width="12" height="12" className="text-error-600" />
                                <p className="text-sm text-error-600">Minimum tenure should be 1 Month</p>
                            </div>
                        </div>

                        <div
                            id="cumulative-interest-section"
                            className="mb-1 flex h-[30px] items-center justify-between"
                        >
                            <p
                                id="cumulative-interest-label"
                                className="text-base font-regular leading-[22.4px] text-neutral-500"
                            >
                                Cumulative interest
                            </p>

                            <Switch
                                id="cumulative-interest-switch"
                                className="data-[state=checked]:bg-brand-primary-500"
                                checked={cumulativeInt}
                                onCheckedChange={(checked: boolean) => {
                                    setCumulativeInt(checked);
                                    debouncedAnalytics("FD_FD_Calculator_Cumulative");
                                }}
                            />
                        </div>

                        <div
                            id="compounding-frequency-section"
                            className="mb-1 flex h-[30px] items-center justify-between"
                        >
                            <p
                                id="compounding-frequency-label"
                                className={`text-base font-regular leading-[22.4px] ${!cumulativeInt ? "text-neutral-300" : "text-neutral-500"}`}
                            >
                                Compounding frequency
                            </p>

                            <Select
                                data-testid="compounding-frequency-select"
                                disabled={!cumulativeInt}
                                value={selectedCompounding}
                                onValueChange={(value) => {
                                    setSelectedCompounding(value);
                                    debouncedAnalytics("FD_FD_Calculator_Compounding");
                                }}
                            >
                                <SelectTrigger
                                    id="compounding-frequency-trigger"
                                    className="h-[30px] font-semibold max-w-max border-none text-lg placeholder:font-regular placeholder:text-neutral-300 md:text-lg [&_svg]:ml-2"
                                >
                                    <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent id="compounding-frequency-content">
                                    {dropdownOptions.map((tenure) => (
                                        <SelectItem
                                            id={`compounding-option-${tenure.key}`}
                                            key={tenure.key}
                                            className="h-16 text-sm md:text-lg"
                                            value={tenure.value}
                                        >
                                            {tenure.value}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div
                        id="donut-chart-container"
                        className="flex w-full flex-col items-center justify-center rounded-[30px] bg-neutral-50 pb-[24px] pt-[32px]"
                    >
                        <DonutChart
                            data-testid="fd-donut-chart"
                            totalInvestment={totalInvestment}
                            interestEarned={interestEarned}
                            renderHeader={renderHeader}
                        />
                    </div>
                </div>
            </div>

            {toggleDownloadApp && <div
                id="download-app-section"
                className="mt-10 flex w-full justify-center px-s9 md:mt-20 md:px-0"
            >
                <RegularButton
                    id="download-app-button"
                    noIcon
                    showRightIcon={true}
                    className="w-full max-w-full md:max-w-fit"
                    rightIcon={
                        <div
                            id="app-store-icons"
                            className="flex h-12 min-w-16 items-center justify-center gap-s4"
                        >
                            <Apple id="apple-store-icon" className="h-10 w-10 text-white" />
                            <div
                                id="store-divider"
                                className="h-full w-[3px] bg-neutralOpacityDark-20"
                            ></div>
                            <GooglePlay id="google-play-icon" className="h-10 w-10 text-white" />
                        </div>
                    }
                    size={ButtonSize.Large}
                    variant={ButtonVariant.PrimaryDefault}
                    label="Download App"
                    onClick={() => toggleDownloadApp("FD_Download_App_Calculator")}
                />
            </div>}
        </div>
    );
};

export default FdCalculator;
